import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { CloseOutlinedIcon } from '../../../assets/mui';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { fiveDecimalNumberWithoutRounding, getRupeeWithDecimal } from '../../../utils/numbers';
import { requestSellMetalOtp, verifySellMetalOtp } from '../useDealDetails';

interface SellMetalOtpDialogProps {
  open: boolean;
  onClose: () => void;
  client: any;
  metalType: 'SILVER' | 'GOLD';
  metalLabel: string;
  quantity: number | string;
  amount: number | string;
  sellPrice: number;
  blockId: string;
  onSuccess: () => void;
}

const SellMetalOtpDialog: React.FC<SellMetalOtpDialogProps> = ({
  open,
  onClose,
  client,
  metalType,
  metalLabel,
  quantity,
  amount,
  sellPrice,
  blockId,
  onSuccess,
}) => {
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(60);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);

  const clientId = client?.clientPublicId;

  const isPayloadValid = useMemo(() => {
    return Boolean(
      clientId && blockId && Number(sellPrice) > 0 && Number(quantity) > 0 && Number(amount) > 0,
    );
  }, [clientId, blockId, sellPrice, quantity, amount]);

  const requestPayload = useMemo(
    () => ({
      price: Number(sellPrice) || 0,
      blockId,
      metalType,
      quantity,
      amount,
      clientPublicId: clientId,
    }),
    [sellPrice, blockId, metalType, quantity, amount, clientId],
  );

  const formatTimer = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${secs}`;
  }, []);

  const triggerOtpRequest = useCallback(async () => {
    if (!isPayloadValid) {
      toast.error('Missing details to request OTP. Please try again.', {
        duration: 2000,
        position: 'top-center',
      });
      throw new Error('Invalid payload');
    }

    await requestSellMetalOtp(requestPayload);
  }, [isPayloadValid, requestPayload]);

  useEffect(() => {
    if (!open) {
      setOtp('');
      setTimer(60);
      setIsRequestingOtp(false);
      setIsResendingOtp(false);
      setIsSubmittingOtp(false);
      return;
    }

    setOtp('');
    setTimer(60);
    setIsRequestingOtp(true);

    triggerOtpRequest()
      .then(() => {
        toast.success(`OTP sent to your registered number`, {
          duration: 2000,
          position: 'top-center',
        });
      })
      .catch(() => {
        toast.error('Unable to request OTP. Please try again.', {
          duration: 2000,
          position: 'top-center',
        });
        onClose();
      })
      .finally(() => {
        setIsRequestingOtp(false);
      });
  }, [open, onClose, triggerOtpRequest]);

  useEffect(() => {
    if (!open || timer <= 0) {
      return;
    }

    const timerId = setTimeout(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearTimeout(timerId);
  }, [open, timer]);

  const handleResendOtp = useCallback(async () => {
    if (timer > 0 || isResendingOtp || isRequestingOtp) {
      return;
    }

    setIsResendingOtp(true);
    try {
      await triggerOtpRequest();
      setOtp('');
      setTimer(60);
      toast.success('OTP resent successfully', {
        duration: 2000,
        position: 'top-center',
      });
    } catch (error) {
      toast.error('Unable to resend OTP. Please try again.', {
        duration: 2000,
        position: 'top-center',
      });
    } finally {
      setIsResendingOtp(false);
    }
  }, [isResendingOtp, isRequestingOtp, timer, triggerOtpRequest]);

  const handleSubmitOtp = useCallback(async () => {
    if (!otp) {
      toast.error('Please enter the OTP', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    if (!isPayloadValid) {
      toast.error('Missing details to verify OTP. Please try again.', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    setIsSubmittingOtp(true);
    verifySellMetalOtp({
      ...requestPayload,
      otp,
    })
      .then(() => {
        toast.success(`${metalLabel} sold successfully`, {
          duration: 2000,
          position: 'top-center',
        });
        onSuccess();
        onClose();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Invalid OTP. Please try again.', {
          duration: 2000,
          position: 'top-center',
        });
      })
      .finally(() => {
        setIsSubmittingOtp(false);
      });
  }, [otp, isPayloadValid, requestPayload, metalLabel, onSuccess, onClose]);

  const contactNumber =
    client?.phone ||
    client?.phoneNumber ||
    client?.mobile ||
    client?.mobileNumber ||
    '+91 XXXXX XXXXX';

  const soldAmount = `${fiveDecimalNumberWithoutRounding(Number(quantity) || 0)}gm`;
  const soldPrice = getRupeeWithDecimal.format(Number(amount) || 0);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle>
        <div className="px-5 flex justify-between mt-2">
          <div>
            <h3 className="text-lg font-semibold">Client Details</h3>
          </div>
          <CloseOutlinedIcon
            className="cursor-pointer bg-slate-200 rounded-full p-1"
            onClick={onClose}
            style={{
              width: '28px',
              height: '28px',
            }}
          />
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="bg-white rounded-lg">
          <div className="p-5 pt-0 pb-3 flex flex-col gap-4">
            {[
              { label: 'Name', value: client?.name || '-' },
              { label: 'Email', value: client?.email || client?.emailId || '-' },
              { label: 'Phone Number', value: contactNumber },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between gap-4">
                <h4 className="text-sm text-gray-500">{label}</h4>
                <h3 className="text-sm text-right text-gray-800">{value}</h3>
              </div>
            ))}
            <div
              className="h-[1px] w-full"
              style={{
                boxSizing: 'border-box',
                backgroundClip: 'content-box',
                backgroundImage:
                  'repeating-linear-gradient(90deg, #0000001F 0, #0000001F 12px, transparent 12px, transparent 18px)',
              }}
            />
            {[
              {
                label: 'Sold Amount',
                value: soldAmount,
              },
              {
                label: 'Sold Price',
                value: soldPrice,
              },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between gap-4">
                <h4 className="text-[10px] uppercase tracking-[0.12em] text-stone-400">{label}</h4>
                <h3 className="text-base font-semibold text-right text-stone-800">{value}</h3>
              </div>
            ))}
          </div>
          <div className="px-5 pt-5 pb-3">
            <div className="flex flex-col gap-4">
              <div>
                <h4 className="text-2xl font-semibold text-gray-950">Enter OTP</h4>
                <p className="text-sm text-gray-400 mt-2">
                  You would have received OTP on{' '}
                  <span className="font-medium text-gray-900">'{contactNumber}'</span>
                </p>
              </div>
              <input
                type="text"
                value={otp}
                onChange={(e) => {
                  const sanitizedValue = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setOtp(sanitizedValue);
                }}
                placeholder="Enter OTP"
                disabled={isRequestingOtp}
                className={clsx(
                  'w-full border border-gray-300 bg-white rounded-xl shadow-sm px-[14px] py-4 text-base focus:outline-none focus:border-gray-400',
                  {
                    'bg-gray-100 cursor-not-allowed text-gray-400': isRequestingOtp,
                  },
                )}
              />
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={timer > 0 || isResendingOtp || isRequestingOtp}
                className={clsx(
                  'text-sm text-gray-500 font-medium text-left underline underline-offset-2',
                  {
                    'text-gray-400 cursor-not-allowed':
                      timer > 0 || isResendingOtp || isRequestingOtp,
                    'text-gray-500': timer === 0 && !isResendingOtp && !isRequestingOtp,
                  },
                )}
              >
                {timer > 0
                  ? `Resend OTP in ${formatTimer(timer)}`
                  : isResendingOtp || isRequestingOtp
                  ? 'Resending...'
                  : 'Resend OTP'}
              </button>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmittingOtp}
                  className={clsx(
                    'flex-1 h-11 rounded-xl border border-gray-200 bg-gray-100 text-gray-600 font-semibold',
                    {
                      'opacity-80 cursor-not-allowed': isSubmittingOtp,
                    },
                  )}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitOtp}
                  disabled={isSubmittingOtp || isRequestingOtp || otp.length < 4}
                  className={clsx(
                    'flex-1 h-11 rounded-xl bg-green-700 text-white font-semibold shadow-sm',
                    {
                      'opacity-80 cursor-not-allowed':
                        isSubmittingOtp || isRequestingOtp || otp.length < 4,
                    },
                  )}
                >
                  {isSubmittingOtp ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SellMetalOtpDialog;
